import { useRef, useState, useCallback, useEffect } from 'react';
import Hls from 'hls.js';

const OPEN_DELAY = 400;   // hovering this long before the popup starts opening
const PLAY_DELAY = 500;  // popup sits on the poster this long before the trailer starts
const CLOSE_GRACE = 150;  // grace period so crossing from tile → popup doesn't snap it shut

/**
 * Drives a Netflix-style floating trailer-preview popup: hover-delay, HLS/video source
 * lifecycle, and viewport-anchored positioning via a portal (so it isn't clipped by a
 * horizontally-scrolling row). One instance per card.
 */
export function useTrailerHoverPreview(trailerUrl) {
  const anchorRef = useRef(null);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [previewReady, setPreviewReady] = useState(false);
  const [canPlay, setCanPlay] = useState(false);
  const [muted, setMuted] = useState(true);

  const close = useCallback(() => {
    clearTimeout(openTimerRef.current);
    clearTimeout(closeTimerRef.current);
    setOpen(false);
    setPreviewReady(false);
    setCanPlay(false);
  }, []);

  const scheduleOpen = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    openTimerRef.current = setTimeout(() => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (r) setRect(r);
      setOpen(true);
    }, OPEN_DELAY);
  }, []);

  const scheduleClose = useCallback(() => {
    clearTimeout(openTimerRef.current);
    closeTimerRef.current = setTimeout(close, CLOSE_GRACE);
  }, [close]);

  const cancelClose = useCallback(() => {
    clearTimeout(closeTimerRef.current);
  }, []);

  // Sit on the poster for a beat after the popup opens before the trailer starts playing.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => setCanPlay(true), PLAY_DELAY);
    return () => clearTimeout(t);
  }, [open]);

  // Attach (and tear down) the trailer source only once the play-delay has elapsed, so
  // idle tiles never open an HLS connection or download video bytes.
  useEffect(() => {
    if (!canPlay || !videoRef.current || !trailerUrl) return undefined;
    const video = videoRef.current;
    const isHls = trailerUrl.includes('.m3u8');
    if (isHls && Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true });
      hls.loadSource(trailerUrl);
      hls.attachMedia(video);
      hlsRef.current = hls;
    } else {
      video.src = trailerUrl;
    }
    video.muted = muted;
    video.play().catch(() => {});
    return () => {
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPlay, trailerUrl]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  // The popup is position:fixed against the viewport rect captured at open-time — if the row
  // (or the page) scrolls or the window resizes while it's open, that anchor goes stale, so
  // just close it rather than show a misaligned popup.
  useEffect(() => {
    if (!open) return undefined;
    const handler = () => close();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open, close]);

  useEffect(() => () => {
    clearTimeout(openTimerRef.current);
    clearTimeout(closeTimerRef.current);
  }, []);

  return {
    anchorRef, videoRef,
    open, rect, previewReady, setPreviewReady,
    muted, setMuted,
    scheduleOpen, scheduleClose, cancelClose, close,
  };
}
